import {
    CreateDimensionInput,
    Dimension,
    Issue,
    LinkRegistry,
    MeasurementInput,
    QuickQuote,
    SubmitQuickQuoteInput,
    UpdateDimensionInput,
} from "../../../API";
import React, {
    ReactNode,
    createContext,
    useContext,
    useMemo,
    useState,
} from 'react';

import { ImageEntity } from "../../../lib/dashboard/types/ImageEntity";

interface IssuesContext {
    linkRegistries: Array<LinkRegistry>;
    issues: Array<Issue>;
    images: Array<ImageEntity>;
    submitIssue?: (locationId: string, description: string, images?: File[], measurement?: MeasurementInput) => Promise<void>;
    onEditIssueSubmit?: (id: string, issue: Issue) => Promise<void>;
    submitQuickQuotes?: (quickQuotes: SubmitQuickQuoteInput[]) => Promise<void>;
    dimensions?: Dimension[];
    onEditDimensionSubmit?: (dimension: UpdateDimensionInput) => Promise<Dimension>;
    parentIdToDimensionMap: Map<string | undefined | null, Dimension>;
    onCreateDimension?: (dimension: CreateDimensionInput) => Promise<Dimension>;
    isQuickQuoteSubmitting: boolean;
    setIsQuickQuoteSubmitting: (isQuickQuoteSubmitting: boolean) => void;
    issueIdToIssueMap: Map<string, Issue>;
    resourceIdToImageMap: Map<string, Array<ImageEntity>>;
    quickQuotes: QuickQuote[] | undefined;
    issueIdToQuickQuoteMap: Map<string, QuickQuote>;
    issueIdToProposalItemPriceTotalMap?: Map<string, number>;
}

const IssuesContext = createContext<IssuesContext | undefined>(undefined);

export const IssuesContextProvider: React.FC<{
    children: ReactNode;
    value: Omit<
        IssuesContext,
        "parentIdToDimensionMap" |
        "isQuickQuoteSubmitting" |
        "setIsQuickQuoteSubmitting" |
        "issueIdToIssueMap" |
        "resourceIdToImageMap" |
        "issueIdToQuickQuoteMap" 
    >
}> = ({ children, value }) => {

    const parentIdToDimensionMap = useMemo(() => (
        new Map<string | undefined | null, Dimension>(
            (value.dimensions ?? []).map((dimension) => [dimension.parentId, dimension]
            ))), [value.dimensions]);

    const issueIdToIssueMap = useMemo(() => new Map<string, Issue>(
        value.issues.map((issue) => [issue.id, issue])
    ), [value.issues]);


    const resourceIdToImageMap = useMemo<Map<string, Array<ImageEntity>>>(() => {
        return value.images.reduce((acc, image) => {
            if (!acc.has(image.parentId)) {
                acc.set(image.parentId, []);
            }
            acc.get(image.parentId)?.push(image);
            return acc;
        }, new Map<string, Array<ImageEntity>>());
    }, [value.images]);

    const issueIdToQuickQuoteMap = useMemo(() => new Map<string, QuickQuote>(
        (value.quickQuotes ?? []).flatMap((quickQuote) =>
            quickQuote.issueIds?.map((issueId) => [issueId, quickQuote] as const) ?? []
        )
    ), [value.quickQuotes]);

    const [isQuickQuoteSubmitting, setIsQuickQuoteSubmitting] = useState(false);

    return (
        <IssuesContext.Provider value={{
            ...value,
            parentIdToDimensionMap,
            isQuickQuoteSubmitting,
            setIsQuickQuoteSubmitting,
            issueIdToIssueMap,
            resourceIdToImageMap,
            issueIdToQuickQuoteMap,
        }}>
            {children}
        </IssuesContext.Provider>
    );
};

export const useIssues = (): IssuesContext => {
    const context = useContext(IssuesContext);
    if (!context) {
        throw new Error('useIssues must be used within a issuesContextProvider');
    }
    return context;
};